import React, { useEffect, useState, useContext } from "react";
import { ArrowUpRight, Funnel, Pencil, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "./AuthContext";
import { API_BASE_URL } from "./api";
import {
  Button,
  Field,
  MetricCard,
  SectionHeader,
  SelectField,
  StatusPill,
  SurfaceCard,
} from "./components/designSystem";
import { ASSET_TYPE_OPTIONS, CURRENCY_OPTIONS } from "./utils/tradeInstrumentSpecs";
import { DualCurrencyValue, formatNaira, formatUsd, getTradeNgnProfit, getTradeUsdProfit, useExchangeRate } from "./utils/currencyDisplay";

export default function TradeList({ refresh, onEdit, onRefresh, filters, setFilters, selectedAccountId }) {
  const navigate = useNavigate();
  const [trades, setTrades] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const { token, user } = useContext(AuthContext);
  const { usdToNgnRate } = useExchangeRate();

  const totalProfitUsd = filtered.reduce((sum, trade) => sum + getTradeUsdProfit(trade, usdToNgnRate), 0);
  const winningTrades = filtered.filter((trade) => getTradeUsdProfit(trade, usdToNgnRate) > 0).length;
  const winRate = filtered.length ? ((winningTrades / filtered.length) * 100).toFixed(1) : "0.0";
  const topAsset = filtered.length
    ? [...filtered].sort((left, right) => getTradeUsdProfit(right, usdToNgnRate) - getTradeUsdProfit(left, usdToNgnRate))[0]?.asset || "-"
    : "-";

  // Fetch trades
  useEffect(() => {
    const url = `${API_BASE_URL}/api/trades${selectedAccountId ? `?accountId=${selectedAccountId}` : ''}`;
    fetch(url, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then((res) => res.json())
      .then((data) => {
        setTrades(data);
        setFiltered(data);
      });
  }, [refresh, token, selectedAccountId]);

  // Apply filters + sorting
  useEffect(() => {
    let data = [...trades];

  if (filters.asset) data = data.filter(t => t.asset.toLowerCase() === filters.asset.toLowerCase());
  if (filters.winLoss === "win") data = data.filter(t => getTradeUsdProfit(t, usdToNgnRate) > 0);
  if (filters.winLoss === "loss") data = data.filter(t => getTradeUsdProfit(t, usdToNgnRate) <= 0);
  if (filters.dateFrom) data = data.filter(t => new Date(t.created_at) >= new Date(filters.dateFrom));
  if (filters.dateTo) data = data.filter(t => new Date(t.created_at) <= new Date(filters.dateTo));

  if (filters.sortBy === "pnl") data.sort((a, b) => getTradeUsdProfit(b, usdToNgnRate) - getTradeUsdProfit(a, usdToNgnRate));
  if (filters.sortBy === "date") data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  if (filters.sortBy === "asset") data.sort((a, b) => a.asset.localeCompare(b.asset));

  setFiltered(data);
  }, [filters, trades, usdToNgnRate]);

  const deleteTrade = async (id) => {
    if (!window.confirm("Delete this trade?")) return;
  await fetch(`${API_BASE_URL}/api/trades/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` }
    });
    onRefresh && onRefresh();
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <SurfaceCard className="overflow-hidden bg-[radial-gradient(circle_at_top_left,_rgba(251,191,36,0.14),_transparent_22%),linear-gradient(135deg,_#ffffff,_#f8fafc)] dark:bg-[radial-gradient(circle_at_top_left,_rgba(251,191,36,0.12),_transparent_22%),linear-gradient(135deg,_#111827,_#0f172a)]">
        <SectionHeader
          eyebrow="Trade Ledger"
          title="Review executions with a cleaner operating view"
          description="Filter, audit, and edit trades without losing context on performance, volume, and market concentration."
          action={
            <Button type="button" variant="primary" onClick={() => navigate("/app/newtrade")}>
              <ArrowUpRight className="h-4 w-4" />
              New Trade
            </Button>
          }
        />
        <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <MetricCard label="Visible trades" value={String(filtered.length)} helper="Current records after filtering." />
          <MetricCard
            label="Visible P&L"
            value={<DualCurrencyValue usdValue={totalProfitUsd} usdToNgnRate={usdToNgnRate} primaryClassName="text-current" secondaryClassName="text-xs text-slate-500 dark:text-slate-400" />}
            helper="USD primary with naira conversion underneath."
            tone={totalProfitUsd >= 0 ? "text-emerald-600 dark:text-emerald-300" : "text-rose-600 dark:text-rose-300"}
          />
          <MetricCard label="Win rate" value={`${winRate}%`} helper="Win ratio within the active filters." />
          <MetricCard label="Top result" value={topAsset} helper="Best-performing asset in the current view." />
        </div>
      </SurfaceCard>

      {/* Trade count progress bar for free users */}
      {(!user?.plan || user.plan === "free") && (() => {
        const now = new Date();
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
        const tradesThisMonth = trades.filter(t => {
          const created = new Date(t.created_at);
          return created >= monthStart && created <= monthEnd;
        });
        const count = tradesThisMonth.length;
        const percent = Math.min((count / 30) * 100, 100);
        let barColor = "bg-green-500";
        if (percent >= 100) barColor = "bg-red-500";
        else if (percent >= 80) barColor = "bg-yellow-500";
        return (
          <SurfaceCard className="border-slate-200 bg-white dark:border-gray-700 dark:bg-gray-800">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Monthly Trade Limit</span>
              <span className="text-xs font-semibold text-gray-700 dark:text-gray-200">{count} of 30 used</span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
              <div className={`h-3 rounded-full transition-all duration-300 ${barColor}`}
                style={{ width: `${percent}%` }}></div>
            </div>
            {percent >= 100 && (
              <p className="text-xs text-red-500 mt-2 font-semibold">You have reached your monthly trade limit. Upgrade to Pro for unlimited trades.</p>
            )}
            {percent >= 80 && percent < 100 && (
              <p className="text-xs text-yellow-600 mt-2 font-semibold">Warning: You are approaching your monthly trade limit.</p>
            )}
          </SurfaceCard>
        );
      })()}

      <SurfaceCard>
        <SectionHeader
          eyebrow="Filters"
          title="Tighten the ledger view"
          description="Slice the trade book by instrument, result, currency, and date so review stays focused."
          action={<StatusPill tone="brand"><Funnel className="mr-2 h-3.5 w-3.5" />Live filters</StatusPill>}
        />
        <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          <Field
            label="Asset"
            placeholder="BTCUSD, XAUUSD, AAPL"
            value={filters.asset}
            onChange={(e) => setFilters({ ...filters, asset: e.target.value })}
          />
          <SelectField
            label="Asset Type"
            value={filters.assetType || ""}
            onChange={(e) => setFilters({ ...filters, assetType: e.target.value })}
          >
            <option value="">All Types</option>
            {ASSET_TYPE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </SelectField>
          <SelectField
            label="Currency"
            value={filters.tradeCurrency || ""}
            onChange={(e) => setFilters({ ...filters, tradeCurrency: e.target.value })}
          >
            <option value="">All Currencies</option>
            {CURRENCY_OPTIONS.map((currency) => (
              <option key={currency} value={currency}>{currency}</option>
            ))}
          </SelectField>
          <SelectField
            label="Outcome"
            value={filters.winLoss}
            onChange={(e) => setFilters({ ...filters, winLoss: e.target.value })}
          >
            <option value="">All Trades</option>
            <option value="win">Winning Trades</option>
            <option value="loss">Losing Trades</option>
          </SelectField>
          <Field
            label="From"
            type="date"
            value={filters.dateFrom}
            onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
          />
          <Field
            label="To"
            type="date"
            value={filters.dateTo}
            onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
          />
          <SelectField
            label="Sort"
            value={filters.sortBy}
            onChange={(e) => setFilters({ ...filters, sortBy: e.target.value })}
          >
            <option value="date">Date</option>
            <option value="pnl">PnL</option>
            <option value="asset">Asset</option>
          </SelectField>
          <div className="flex items-end">
            <Button
              type="button"
              variant="secondary"
              className="w-full"
              onClick={() => setFilters({ asset: "", assetType: "", tradeCurrency: "", winLoss: "", dateFrom: "", dateTo: "", sortBy: "date" })}
            >
              Reset Filters
            </Button>
          </div>
        </div>
      </SurfaceCard>

      {/* Trades list - responsive cards */}
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        {filtered
          .filter(t => !filters.assetType || t.assetType === filters.assetType)
          .filter(t => !filters.tradeCurrency || t.tradeCurrency === filters.tradeCurrency)
          .map((t) => (
            <SurfaceCard key={t._id} className="flex min-h-[240px] flex-col justify-between bg-white dark:bg-gray-800">
              <div>
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.26em] text-slate-400">{new Date(t.created_at).toLocaleDateString()}</p>
                    <h3 className="mt-2 text-2xl font-semibold text-slate-950 dark:text-white">{t.asset}</h3>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <StatusPill tone="neutral">{t.assetType?.toUpperCase()}</StatusPill>
                      <StatusPill tone="brand">{t.tradeCurrency}</StatusPill>
                      <StatusPill tone={t.direction === "long" ? "success" : "warning"}>{t.direction}</StatusPill>
                      <StatusPill tone={t.source === "mekfunded_mt5" ? "brand" : "neutral"}>
                        {t.source === "mekfunded_mt5" ? "Imported MT5" : "Manual"}
                      </StatusPill>
                    </div>
                    {t.source === "mekfunded_mt5" ? (
                      <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                        {t.externalAccountLogin ? `Login ${t.externalAccountLogin}` : "Imported trade"}
                        {t.externalServer ? ` · ${t.externalServer}` : ""}
                        {t.importedAt ? ` · Synced ${new Date(t.importedAt).toLocaleString()}` : ""}
                      </p>
                    ) : null}
                  </div>
                  <div className="text-right">
                    <p className={`text-2xl font-semibold ${getTradeUsdProfit(t, usdToNgnRate) >= 0 ? "text-emerald-600 dark:text-emerald-300" : "text-rose-600 dark:text-rose-300"}`}>
                      {formatUsd(getTradeUsdProfit(t, usdToNgnRate))}
                    </p>
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{formatNaira(getTradeNgnProfit(t, usdToNgnRate))}</p>
                    {typeof t.profit === "number" && !isNaN(t.profit) && t.tradeCurrency && t.tradeCurrency !== "USD" ? (
                      <p className="text-xs text-slate-500 dark:text-slate-400">Original market result: {`${t.profit.toFixed(2)} ${t.tradeCurrency}`}</p>
                    ) : null}
                  </div>
                </div>

                <div className="mt-5 grid gap-3 sm:grid-cols-3">
                  <MetricCard label="Entry" value={String(t.entry_price ?? "-")} className="p-3" />
                  <MetricCard label="Exit" value={String(t.exit_price ?? "-")} className="p-3" />
                  <MetricCard label={t.lot_size ? "Lot Size" : "Quantity"} value={String(t.lot_size || t.quantity || "-")} className="p-3" />
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  {t.ruleAdherence ? <StatusPill tone={t.ruleAdherence === "followed" ? "success" : t.ruleAdherence === "partial" ? "warning" : "danger"}>{t.ruleAdherence}</StatusPill> : null}
                  {t.executionQuality ? <StatusPill tone="neutral">{t.executionQuality}</StatusPill> : null}
                  {t.confidenceScore ? <StatusPill tone="brand">Confidence {t.confidenceScore}/10</StatusPill> : null}
                </div>

                {Array.isArray(t.setupTags) && t.setupTags.length ? (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {t.setupTags.map((tag) => (
                      <StatusPill key={tag} tone="brand">{tag}</StatusPill>
                    ))}
                  </div>
                ) : null}

                {Array.isArray(t.mistakeTags) && t.mistakeTags.length ? (
                  <div className="mt-4 rounded-3xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-500/20 dark:bg-amber-500/10">
                    <p className="text-xs font-semibold uppercase tracking-[0.26em] text-amber-700 dark:text-amber-300">Mistakes tracked</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {t.mistakeTags.map((tag) => (
                        <StatusPill key={tag} tone="warning">{tag}</StatusPill>
                      ))}
                    </div>
                  </div>
                ) : null}

                {t.setupDescription || t.lessonsLearned ? (
                  <div className="mt-4 grid gap-3 md:grid-cols-2">
                    {t.setupDescription ? (
                      <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-gray-700 dark:bg-gray-900">
                        <p className="text-xs font-semibold uppercase tracking-[0.26em] text-slate-400">Setup</p>
                        <p className="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">{t.setupDescription}</p>
                      </div>
                    ) : null}
                    {t.lessonsLearned ? (
                      <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-gray-700 dark:bg-gray-900">
                        <p className="text-xs font-semibold uppercase tracking-[0.26em] text-slate-400">Lessons</p>
                        <p className="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">{t.lessonsLearned}</p>
                      </div>
                    ) : null}
                  </div>
                ) : null}

                {Array.isArray(t.screenshots) && t.screenshots.length ? (
                  <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-3">
                    {t.screenshots.slice(0, 3).map((shot) => (
                      <a key={shot.url} href={shot.url} target="_blank" rel="noreferrer" className="overflow-hidden rounded-2xl border border-slate-200 dark:border-gray-700">
                        <img src={shot.url} alt={shot.name || "Trade screenshot"} className="h-24 w-full object-cover" />
                      </a>
                    ))}
                  </div>
                ) : null}

                {t.notes ? (
                  <div className="mt-5 rounded-3xl border border-slate-200 bg-slate-50 p-4 text-sm leading-7 text-slate-600 dark:border-gray-700 dark:bg-gray-900 dark:text-slate-300">
                    {t.notes}
                  </div>
                ) : null}
              </div>

              <div className="mt-6 flex flex-wrap gap-3">
                <Button
                  type="button"
                  variant="brand"
                  className="flex-1"
                  onClick={() => { onEdit(t); navigate("/app/newtrade"); }}
                >
                  <Pencil className="h-4 w-4" />
                  Edit
                </Button>
                <Button
                  type="button"
                  variant="danger"
                  className="flex-1"
                  onClick={() => deleteTrade(t._id)}
                >
                  <Trash2 className="h-4 w-4" />
                  Delete
                </Button>
              </div>
            </SurfaceCard>
          ))}
      </div>
      {filtered.length === 0 && (
        <SurfaceCard>
          <p className="text-center text-sm font-medium text-slate-500 dark:text-slate-400">No trades found for the current filters.</p>
        </SurfaceCard>
      )}
    </div>
  );
}